import React, { type JSX } from "react";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { MAX_PARAGRAPH_SELECTIONS } from "../../DraftSelectionSlice_Store";

/** Selection count plus the clear affordance. */
function SelectionControls({
  selectedCount,
  onClearSelections,
}: {
  selectedCount: number;
  onClearSelections: () => void;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between bg-cvc-lb2 border border-cvc-lb1 rounded-lg px-3 py-2">
      <div className="flex items-center space-x-3">
        <span className="text-sm font-medium text-gray-700">
          {selectedCount > 0 ? (
            <span className="text-cvc-blue">
              {selectedCount} paragraph{selectedCount !== 1 ? "s" : ""} selected (max{" "}
              {MAX_PARAGRAPH_SELECTIONS})
            </span>
          ) : (
            <span className="text-gray-500">
              No paragraphs selected (max {MAX_PARAGRAPH_SELECTIONS})
            </span>
          )}
        </span>
      </div>
      <div className="flex items-center space-x-2">
        {selectedCount > 0 && (
          <button
            onClick={onClearSelections}
            className="text-xs text-gray-600 hover:text-gray-700 font-medium"
          >
            Clear Selection
          </button>
        )}
      </div>
    </div>
  );
}

/** Title bar, expand/collapse controls and the selection summary. */
export default function DraftPreviewHeader({
  paragraphCount,
  selectedCount,
  onToggleAll,
  onClearSelections,
}: {
  paragraphCount: number;
  selectedCount: number;
  onToggleAll: (expand: boolean) => void;
  onClearSelections: () => void;
}): JSX.Element {
  return (
    <div className="p-4 border-b border-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center space-x-2 min-w-0">
          <DocumentTextIcon className="h-5 w-5 text-cvc-blue flex-shrink-0" />
          <h2 className="text-base font-semibold text-gray-900 whitespace-nowrap">Draft Content</h2>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            ({paragraphCount} paragraphs)
          </span>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={() => onToggleAll(true)}
            className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
          >
            Expand All
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => onToggleAll(false)}
            className="text-xs text-cvc-blue hover:text-cvc-blue/80 font-medium"
          >
            Collapse All
          </button>
        </div>
      </div>

      <SelectionControls selectedCount={selectedCount} onClearSelections={onClearSelections} />
    </div>
  );
}
